import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../../APIs";

import { Card, CardContent } from "../../../ui/common-ui/card";
import { Input } from "../../../ui/common-ui/input";
import { Badge } from "../../../ui/common-ui/badge";

import {
    ArrowLeft,
    Search,
    RefreshCw,
    Users,
    Shield,
    ExternalLink,
    X,
    Inbox,
    User,
    Eye,
    GitBranch,
    Building2,
    UserCheck,
    FolderGit2,
    Database,
} from "lucide-react";
import { FaGithub } from "react-icons/fa6";
import { Button } from "../../../ui/common-ui/button";
import GithubEntityAnalytics from "./GithubEntityAnalytics";

type TenantData = any;

type GithubInstallation = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    installationId: number;
    appId: number;
    appSlug: string;
    installationTargetType: string;
    installationTargetId: number;
    syncEnabled: boolean;
    lastSyncStatus: string;
    lastSyncError: string | null;
    lastSyncAt: string | null;
};

type GithubAccount = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    githubId: number;
    name: string;
    htmlUrl: string;
    description: string | null;
    createdAt: string;
    updatedAt: string;
};

type GithubRepo = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    repoId: number;
    name: string;
    fullName: string;
    htmlUrl: string;
    isPrivate: boolean;
    archived: boolean;
    fork: boolean;
    defaultBranch: string;
    createdAt: string;
    updatedAt: string;
};

type GithubUser = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    githubId: number;
    login: string;
    htmlUrl: string;
    type: string;
    createdAt: string;
    updatedAt: string;
};

type GithubTeam = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    teamId: number;
    slug: string;
    name: string;
    htmlUrl: string;
    privacy: string;
    createdAt: string;
    updatedAt: string;
};

type GithubTeamMembership = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    teamId: number;
    userGithubId: number;
    userLogin: string;
    role: string;
    createdAt: string;
    updatedAt: string;
};

type GithubRepoCollaborator = {
    id: number;
    tenantName: string;
    instanceUrl: string;
    orgLogin: string;
    repoId: number;
    userGithubId: number;
    userLogin: string;
    permission: string;
    createdAt: string;
    updatedAt: string;
};

export const fetchTenants = async (): Promise<TenantData[]> => {
    const response = await axios.get(`${NGROK}/api/get-all-domains`, {});
    return response.data || [];
};

export const fetchGithubInstallations = async (tenantName: string, instanceUrl?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/installations`, {
        params: { tenantName, instanceUrl },
    });
    return response.data || [];
};

export const fetchGithubAccounts = async (tenantName: string, instanceUrl?: string, orgLogin?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/accounts`, {
        params: { tenantName, instanceUrl, orgLogin },
    });
    return response.data || [];
};

export const fetchGithubRepos = async (tenantName: string, instanceUrl?: string, orgLogin?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/repos`, {
        params: { tenantName, instanceUrl, orgLogin },
    });
    return response.data || [];
};

export const fetchGithubUsers = async (tenantName: string, instanceUrl?: string, orgLogin?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/users`, {
        params: { tenantName, instanceUrl, orgLogin },
    });
    return response.data || [];
};

export const fetchGithubTeams = async (tenantName: string, instanceUrl?: string, orgLogin?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/teams`, {
        params: { tenantName, instanceUrl, orgLogin },
    });
    return response.data || [];
};

export const fetchGithubTeamMemberships = async (tenantName: string, instanceUrl?: string, orgLogin?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/team-memberships`, {
        params: { tenantName, instanceUrl, orgLogin },
    });
    return response.data || [];
};

export const fetchGithubRepoCollaborators = async (tenantName: string, instanceUrl?: string, orgLogin?: string) => {
    const response = await axios.get(`${NGROK}/api/saas/github/data/repo-collaborators`, {
        params: { tenantName, instanceUrl, orgLogin },
    });
    return response.data || [];
};

function safeText(v: any) {
    if (v === null || v === undefined) return "";
    return String(v);
}

function extractTenantName(t: any): string {
    return t?.tenantName || t?.name || t?.domain || t?.tenant || t?.subdomain || t?.id || "";
}

function formatDate(v: any) {
    const s = safeText(v);
    if (!s) return "-";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString();
}

const getActivityColor = (count: number) => {
    if (count < 10) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
    if (count <= 50) return "bg-gradient-to-r from-orange-400 to-orange-500";
    return "bg-gradient-to-r from-orange-600 to-orange-700";
};

const getActivityIntensity = (count: number) => {
    return 0;
};

function ActivityBar({ count, className = "" }: { count: number; className?: string }) {
    const intensity = getActivityIntensity(count);
    const colorClass = getActivityColor(count);

    return (
        <div className={`relative h-2 bg-muted rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${intensity}%` }}
            />
        </div>
    );
}

const SECTION_ORDER: SectionKey[] = ["users", "repoCollaborators", "repos", "teams", "teamMemberships", "accounts"];

const SECTION_CONFIG = {
    installations: {
        icon: Database,
        label: "Installations",
        gradient: "from-gray-500/15 to-gray-600/5",
        border: "border-l-gray-500",
        iconBg: "bg-gray-500/10",
        iconColor: "text-gray-600 dark:text-gray-400",
        activeBg: "bg-gray-500",
        activeRing: "ring-gray-500/20",
    },
    accounts: {
        icon: Building2,
        label: "Accounts",
        gradient: "from-blue-500/15 to-blue-600/5",
        border: "border-l-blue-500",
        iconBg: "bg-blue-500/10",
        iconColor: "text-blue-600 dark:text-blue-400",
        activeBg: "bg-blue-500",
        activeRing: "ring-blue-500/20",
    },
    repos: {
        icon: FolderGit2,
        label: "Repos",
        gradient: "from-violet-500/15 to-violet-600/5",
        border: "border-l-violet-500",
        iconBg: "bg-violet-500/10",
        iconColor: "text-violet-600 dark:text-violet-400",
        activeBg: "bg-violet-500",
        activeRing: "ring-violet-500/20",
    },
    users: {
        icon: Users,
        label: "Users",
        gradient: "from-emerald-500/15 to-emerald-600/5",
        border: "border-l-emerald-500",
        iconBg: "bg-emerald-500/10",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        activeBg: "bg-emerald-500",
        activeRing: "ring-emerald-500/20",
    },
    teams: {
        icon: Shield,
        label: "Teams",
        gradient: "from-amber-500/15 to-amber-600/5",
        border: "border-l-amber-500",
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-600 dark:text-amber-400",
        activeBg: "bg-amber-500",
        activeRing: "ring-amber-500/20",
    },
    teamMemberships: {
        icon: UserCheck,
        label: "Team Members",
        gradient: "from-pink-500/15 to-pink-600/5",
        border: "border-l-pink-500",
        iconBg: "bg-pink-500/10",
        iconColor: "text-pink-600 dark:text-pink-400",
        activeBg: "bg-pink-500",
        activeRing: "ring-pink-500/20",
    },
    repoCollaborators: {
        icon: GitBranch,
        label: "Collaborators",
        gradient: "from-cyan-500/15 to-cyan-600/5",
        border: "border-l-cyan-500",
        iconBg: "bg-cyan-500/10",
        iconColor: "text-cyan-600 dark:text-cyan-400",
        activeBg: "bg-cyan-500",
        activeRing: "ring-cyan-500/20",
    },
} as const;

type SectionKey = keyof typeof SECTION_CONFIG;

function SkeletonCard() {
    return (
        <div className="rounded-xl border border-border p-4 space-y-3 animate-pulse">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                </div>
                <div className="h-8 w-16 rounded-md bg-muted" />
            </div>
        </div>
    );
}

function EmptyState({ section, query }: { section: string; query: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mb-4">
                <Inbox className="w-8 h-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
                {query ? "No matching results" : `No ${section} found`}
            </h3>
            <p className="text-xs text-muted-foreground max-w-[280px]">
                {query ? `No ${section} match "${query}". Try a different search term.` : `There are no ${section} synced for this tenant yet.`}
            </p>
        </div>
    );
}

type SelectedEntity = {
    type: "user" | "repo" | "team" | "account";
    data: any;
    entityValue: string;
    displayName: string;
};

export default function GithubData({ onBack }: { onBack: () => void }) {
    const [tenants, setTenants] = useState<TenantData[]>([]);
    const [tenantName, setTenantName] = useState<string>("");
    const [instanceUrl] = useState<string>("https://github.com");
    const [loading, setLoading] = useState<boolean>(false);

    const [installations, setInstallations] = useState<GithubInstallation[]>([]);
    const [accounts, setAccounts] = useState<GithubAccount[]>([]);
    const [repos, setRepos] = useState<GithubRepo[]>([]);
    const [users, setUsers] = useState<GithubUser[]>([]);
    const [teams, setTeams] = useState<GithubTeam[]>([]);
    const [teamMemberships, setTeamMemberships] = useState<GithubTeamMembership[]>([]);
    const [repoCollaborators, setRepoCollaborators] = useState<GithubRepoCollaborator[]>([]);

    const [activeSection, setActiveSection] = useState<SectionKey>("users");
    const [query, setQuery] = useState<string>("");
    const [orgLoginFilter, setOrgLoginFilter] = useState<string>("");

    // Analytics view state
    const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);

    // Derive unique orgLogins from installations
    const orgLogins = useMemo(() => {
        const set = new Set<string>();
        installations.forEach((i) => { if (i.orgLogin) set.add(i.orgLogin); });
        return Array.from(set);
    }, [installations]);

    const filteredInstallations = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = installations;
        if (orgLoginFilter) list = list.filter((i) => i.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (i) =>
                safeText(i.orgLogin).toLowerCase().includes(q) ||
                safeText(i.appSlug).toLowerCase().includes(q) ||
                safeText(i.installationId).toLowerCase().includes(q) ||
                safeText(i.lastSyncStatus).toLowerCase().includes(q)
        );
    }, [installations, query, orgLoginFilter]);

    const filteredAccounts = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = accounts;
        if (orgLoginFilter) list = list.filter((a) => a.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (a) =>
                safeText(a.name).toLowerCase().includes(q) ||
                safeText(a.orgLogin).toLowerCase().includes(q) ||
                safeText(a.githubId).toLowerCase().includes(q)
        );
    }, [accounts, query, orgLoginFilter]);

    const filteredRepos = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = repos;
        if (orgLoginFilter) list = list.filter((r) => r.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (r) =>
                safeText(r.name).toLowerCase().includes(q) ||
                safeText(r.fullName).toLowerCase().includes(q) ||
                safeText(r.repoId).toLowerCase().includes(q) ||
                safeText(r.defaultBranch).toLowerCase().includes(q)
        );
    }, [repos, query, orgLoginFilter]);

    const filteredUsers = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = users;
        if (orgLoginFilter) list = list.filter((u) => u.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (u) =>
                safeText(u.login).toLowerCase().includes(q) ||
                safeText(u.githubId).toLowerCase().includes(q) ||
                safeText(u.type).toLowerCase().includes(q)
        );
    }, [users, query, orgLoginFilter]);

    const filteredTeams = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = teams;
        if (orgLoginFilter) list = list.filter((t) => t.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (t) =>
                safeText(t.name).toLowerCase().includes(q) ||
                safeText(t.slug).toLowerCase().includes(q) ||
                safeText(t.teamId).toLowerCase().includes(q)
        );
    }, [teams, query, orgLoginFilter]);

    const filteredTeamMemberships = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = teamMemberships;
        if (orgLoginFilter) list = list.filter((tm) => tm.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (tm) =>
                safeText(tm.userLogin).toLowerCase().includes(q) ||
                safeText(tm.role).toLowerCase().includes(q) ||
                safeText(tm.teamId).toLowerCase().includes(q)
        );
    }, [teamMemberships, query, orgLoginFilter]);

    const filteredRepoCollaborators = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = repoCollaborators;
        if (orgLoginFilter) list = list.filter((rc) => rc.orgLogin === orgLoginFilter);
        if (!q) return list;
        return list.filter(
            (rc) =>
                safeText(rc.userLogin).toLowerCase().includes(q) ||
                safeText(rc.permission).toLowerCase().includes(q) ||
                safeText(rc.repoId).toLowerCase().includes(q)
        );
    }, [repoCollaborators, query, orgLoginFilter]);

    const rowsForSection = () => {
        switch (activeSection) {
            case "installations": return filteredInstallations;
            case "accounts": return filteredAccounts;
            case "repos": return filteredRepos;
            case "users": return filteredUsers;
            case "teams": return filteredTeams;
            case "teamMemberships": return filteredTeamMemberships;
            case "repoCollaborators": return filteredRepoCollaborators;
        }
    };

    const counts: Record<SectionKey, number> = {
        installations: installations.length,
        accounts: accounts.length,
        repos: repos.length,
        users: users.length,
        teams: teams.length,
        teamMemberships: teamMemberships.length,
        repoCollaborators: repoCollaborators.length,
    };

    const loadAll = async (tn: string) => {
        setLoading(true);
        try {
            const ol = orgLoginFilter || undefined;
            const [inst, accs, rps, usrs, tms, tmMem, rcols] = await Promise.all([
                fetchGithubInstallations(tn, instanceUrl),
                fetchGithubAccounts(tn, instanceUrl, ol),
                fetchGithubRepos(tn, instanceUrl, ol),
                fetchGithubUsers(tn, instanceUrl, ol),
                fetchGithubTeams(tn, instanceUrl, ol),
                fetchGithubTeamMemberships(tn, instanceUrl, ol),
                fetchGithubRepoCollaborators(tn, instanceUrl, ol),
            ]);

            setInstallations(inst || []);
            setAccounts(accs || []);
            setRepos(rps || []);
            setUsers(usrs || []);
            setTeams(tms || []);
            setTeamMemberships(tmMem || []);
            setRepoCollaborators(rcols || []);
        } catch {
            setInstallations([]);
            setAccounts([]);
            setRepos([]);
            setUsers([]);
            setTeams([]);
            setTeamMemberships([]);
            setRepoCollaborators([]);
        } finally {
            setLoading(false);
        }
    };

    const init = async () => {
        const t = await fetchTenants();
        setTenants(t || []);
        const first = (t || []).map(extractTenantName).find(Boolean) || "";
        setTenantName(first);
        if (first) await loadAll(first);
    };

    useEffect(() => {
        init();
    }, []);

    const changeTenant = async (tn: string) => {
        setTenantName(tn);
        setQuery("");
        setOrgLoginFilter("");
        setSelectedEntity(null);
        await loadAll(tn);
    };

    const refresh = async () => {
        if (!tenantName) return;
        await loadAll(tenantName);
    };

    const openLink = (url: string) => {
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleCardClick = (row: any) => {
        if (activeSection === "users") {
            const u = row as GithubUser;
            setSelectedEntity({
                type: "user",
                data: u,
                entityValue: `USER:${u.githubId}`,
                displayName: u.login || String(u.githubId),
            });
        } else if (activeSection === "repos") {
            const r = row as GithubRepo;
            setSelectedEntity({
                type: "repo",
                data: r,
                entityValue: `REPO:${r.repoId}`,
                displayName: r.fullName || r.name || String(r.repoId),
            });
        } else if (activeSection === "teams") {
            const t = row as GithubTeam;
            setSelectedEntity({
                type: "team",
                data: t,
                entityValue: `TEAM:${t.teamId}`,
                displayName: t.name || t.slug || String(t.teamId),
            });
        } else if (activeSection === "accounts") {
            const a = row as GithubAccount;
            setSelectedEntity({
                type: "account",
                data: a,
                entityValue: `ACCOUNT:${a.githubId}`,
                displayName: a.name || a.orgLogin || String(a.githubId),
            });
        }
    };

    // If an entity is selected, show the analytics page
    if (selectedEntity) {
        return (
            <GithubEntityAnalytics
                tenantName={tenantName}
                instanceUrl={instanceUrl}
                endpointType={selectedEntity.type}
                entityValue={selectedEntity.entityValue}
                displayName={selectedEntity.displayName}
                entityData={selectedEntity.data}
                onBack={() => setSelectedEntity(null)}
            />
        );
    }

    const renderRowCard = (row: any, index: number) => {
        const key = `${activeSection}:${row.id || index}`;

        if (activeSection === "installations") {
            const i = row as GithubInstallation;
            const isSync = i.syncEnabled;
            const statusOk = i.lastSyncStatus === "success" || i.lastSyncStatus === "completed";

            return (
                <Card key={key} className="border-border portal-card-shadow portal-card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Database className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{i.orgLogin || "-"}</h3>
                                    <Badge
                                        variant={isSync ? "default" : "secondary"}
                                        className={`text-xs h-5 ${!isSync ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" : ""}`}
                                    >
                                        {isSync ? "Sync On" : "Sync Off"}
                                    </Badge>
                                    {i.lastSyncStatus && (
                                        <Badge
                                            variant={statusOk ? "default" : "secondary"}
                                            className={`text-xs h-5 ${!statusOk ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" : ""}`}
                                        >
                                            {i.lastSyncStatus}
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    App: {i.appSlug || "-"} · Install #{safeText(i.installationId)} · {i.installationTargetType || "-"}
                                </p>
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Last Sync</div>
                                <div className="text-xs font-medium">{formatDate(i.lastSyncAt)}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Target ID</div>
                                <div className="text-xs font-medium">{safeText(i.installationTargetId) || "-"}</div>
                            </div>
                            {i.lastSyncError && (
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-muted-foreground mb-1.5">Error</div>
                                    <div className="text-xs font-medium text-destructive truncate">{i.lastSyncError}</div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "accounts") {
            const a = row as GithubAccount;
            return (
                <Card
                    key={key}
                    className="cursor-pointer border-border portal-card-shadow portal-card-hover"
                    onClick={() => handleCardClick(a)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{a.name || a.orgLogin || "-"}</h3>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    @{a.orgLogin || "-"} · ID {safeText(a.githubId) || "-"}
                                </p>
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {a.htmlUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(a.htmlUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Description</div>
                                <div className="text-xs font-medium truncate">{a.description || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(a.createdAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Updated</div>
                                <div className="text-xs font-medium">{formatDate(a.updatedAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "repos") {
            const r = row as GithubRepo;
            const activity = r.repoId % 100;

            return (
                <Card
                    key={key}
                    className="cursor-pointer border-border portal-card-shadow portal-card-hover"
                    onClick={() => handleCardClick(r)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <FolderGit2 className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{r.name || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{r.isPrivate ? "private" : "public"}</Badge>
                                    {r.archived && (
                                        <Badge variant="secondary" className="text-[10px] h-5 font-medium">archived</Badge>
                                    )}
                                    {r.fork && (
                                        <Badge variant="secondary" className="text-[10px] h-5 font-medium">fork</Badge>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    {r.fullName || "-"} · ID {safeText(r.repoId) || "-"}
                                </p>
                            </div>

                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <div className="text-xs text-muted-foreground">Activity</div>
                                <ActivityBar count={activity} className="w-20" />
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {r.htmlUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(r.htmlUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Default Branch</div>
                                <div className="text-xs font-medium">{r.defaultBranch || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Org</div>
                                <div className="text-xs font-medium">{r.orgLogin || "-"}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(r.createdAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "users") {
            const u = row as GithubUser;
            const activity = u.githubId % 100;

            return (
                <Card
                    key={key}
                    className="cursor-pointer border-border portal-card-shadow portal-card-hover"
                    onClick={() => handleCardClick(u)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{u.login || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{u.type || "User"}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    ID {safeText(u.githubId) || "-"} · {u.orgLogin || "-"}
                                </p>
                            </div>

                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <div className="text-xs text-muted-foreground">Activity</div>
                                <ActivityBar count={activity} className="w-20" />
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {u.htmlUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(u.htmlUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Org</div>
                                <div className="text-xs font-medium">{u.orgLogin || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(u.createdAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Updated</div>
                                <div className="text-xs font-medium">{formatDate(u.updatedAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "teams") {
            const t = row as GithubTeam;

            return (
                <Card
                    key={key}
                    className="cursor-pointer border-border portal-card-shadow portal-card-hover"
                    onClick={() => handleCardClick(t)}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{t.name || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{t.privacy || "unknown"}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    @{t.slug || "-"} · ID {safeText(t.teamId) || "-"} · {t.orgLogin || "-"}
                                </p>
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {t.htmlUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            openLink(t.htmlUrl);
                                        }}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Eye className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Org</div>
                                <div className="text-xs font-medium">{t.orgLogin || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(t.createdAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Updated</div>
                                <div className="text-xs font-medium">{formatDate(t.updatedAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "teamMemberships") {
            const tm = row as GithubTeamMembership;

            return (
                <Card key={key} className="border-border portal-card-shadow portal-card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <UserCheck className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{tm.userLogin || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{tm.role || "member"}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    Team #{safeText(tm.teamId)} · User #{safeText(tm.userGithubId)} · {tm.orgLogin || "-"}
                                </p>
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Org</div>
                                <div className="text-xs font-medium">{tm.orgLogin || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(tm.createdAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Updated</div>
                                <div className="text-xs font-medium">{formatDate(tm.updatedAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (activeSection === "repoCollaborators") {
            const rc = row as GithubRepoCollaborator;

            return (
                <Card key={key} className="border-border portal-card-shadow portal-card-hover">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <GitBranch className="w-4 h-4 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-sm text-foreground truncate">{rc.userLogin || "-"}</h3>
                                    <Badge variant="outline" className="text-[10px] h-5 font-medium">{rc.permission || "read"}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                    Repo #{safeText(rc.repoId)} · User #{safeText(rc.userGithubId)} · {rc.orgLogin || "-"}
                                </p>
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Org</div>
                                <div className="text-xs font-medium">{rc.orgLogin || "-"}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-muted-foreground mb-1.5">Created</div>
                                <div className="text-xs font-medium">{formatDate(rc.createdAt)}</div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                                <div className="text-xs text-muted-foreground mb-1.5">Updated</div>
                                <div className="text-xs font-medium">{formatDate(rc.updatedAt)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        return null;
    };

    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <FaGithub className="w-8 h-8 text-primary" />
                    <div className="flex-1">
                        <h1 className="text-2xl font-semibold text-foreground">GitHub Data</h1>
                        <p className="text-sm text-muted-foreground">Synced entities for your configured GitHub tenant</p>
                    </div>
                    <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground" onClick={onBack}>
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                </div>

                {/* Filters Row */}
                <div className="flex items-center gap-4 mb-6 flex-wrap">
                    {/* Tenant Dropdown */}
                    <div>
                        <label className="text-sm font-medium mb-2 block text-muted-foreground">Tenant</label>
                        <select
                            className="h-10 rounded-lg border border-border bg-background px-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[180px]"
                            value={tenantName}
                            onChange={(e) => changeTenant(e.target.value)}
                            data-testid="select-tenant"
                        >
                            {tenants.map((t, idx) => {
                                const tn = extractTenantName(t);
                                return (
                                    <option key={`${tn}-${idx}`} value={tn}>{tn}</option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Org Login Filter */}
                    {orgLogins.length > 0 && (
                        <div>
                            <label className="text-sm font-medium mb-2 block text-muted-foreground">Org / Account</label>
                            <select
                                className="h-10 rounded-lg border border-border bg-background px-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[160px]"
                                value={orgLoginFilter}
                                onChange={(e) => setOrgLoginFilter(e.target.value)}
                            >

                                {orgLogins.map((ol) => (
                                    <option key={ol} value={ol}>{ol}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Section Tabs */}
                    <div>
                        <label className="text-sm font-medium mb-2 block text-muted-foreground opacity-0 select-none">Section</label>
                        <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border">
                            {SECTION_ORDER.map((sKey) => {
                                const sc = SECTION_CONFIG[sKey];
                                const SIcon = sc.icon;
                                const isActiveTab = activeSection === sKey;

                                return (
                                    <button
                                        key={sKey}
                                        onClick={() => setActiveSection(sKey)}
                                        className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${isActiveTab
                                            ? "bg-background text-foreground shadow-sm border border-border/50"
                                            : "text-foreground hover:text-foreground hover:bg-background/50"
                                            }`}
                                    >
                                        <SIcon className="w-3.5 h-3.5" />
                                        {sc.label}
                                        <span
                                            className={`ml-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold tabular-nums ${isActiveTab ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                                        >
                                            {counts[sKey]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <label className="text-sm font-medium mb-2 block text-muted-foreground">Search</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder={`Search ${activeSection}...`}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="pl-10 pr-10"
                                data-testid="input-search-github-data"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Refresh Button */}
                    <div>
                        <label className="text-sm font-medium mb-2 block text-muted-foreground opacity-0 select-none">Actions</label>
                        <Button onClick={refresh} disabled={loading || !tenantName} className="gap-2">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Data List */}
                <div className="space-y-2">
                    {loading ? (
                        Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
                    ) : rowsForSection().length === 0 ? (
                        <EmptyState section={activeSection} query={query} />
                    ) : (
                        rowsForSection().map((row, index) => renderRowCard(row, index))
                    )}
                </div>
            </div>
        </div>
    );
}
